/**
 * Dion's AI - Wrapper for the existing MCTS_v2 Priority System
 *
 * This AI uses a sophisticated 29-priority decision engine that:
 * - Analyzes tactical patterns (race victories, king safety, captures)
 * - Uses early exit for forced moves
 * - Falls back to Focused MCTS for complex positions
 *
 * This is the baseline AI that we're building competitors for!
 */

import { GameState, IMove, Position, PieceType } from "../../types";
import { IAIPlayer, AIMoveResult, AIStats, AIPlayerType } from "../Core/IAIPlayer";
import { ZodiacEngine } from "../../zodiacEngine";
import { MCTSController_v2 } from "../../MCTS_v2/core/MCTSController_v2";

export class DionsAI implements IAIPlayer {
  readonly name = AIPlayerType.DIONS_AI;
  readonly displayName = "Dion's AI";
  readonly description = "29-priority decision engine with Focused MCTS fallback";

  private mctsController: MCTSController_v2;
  private gameEngine: ZodiacEngine;
  private stats: AIStats = {
    movesCalculated: 0,
    averageThinkingTime: 0,
    nodesEvaluated: 0,
  };
  private totalThinkingTime = 0;

  constructor(gameEngine: ZodiacEngine) {
    this.gameEngine = gameEngine;
    this.mctsController = new MCTSController_v2(gameEngine, false);
  }

  /**
   * Select king - placeholder for now (will use C# heuristic logic later)
   */
  selectKing(availableKings: PieceType[], isLightSide: boolean): PieceType {
    // Placeholder: random selection
    // TODO: Implement C# piece selection heuristic from old version
    const randomIndex = Math.floor(Math.random() * availableKings.length);
    return availableKings[randomIndex];
  }

  async selectMove(gameState: GameState, maxThinkingTime: number): Promise<AIMoveResult> {
    const startTime = Date.now();

    // Use the existing MCTS controller to find the best move
    // The controller already handles timing and iteration limits
    const iterations = this.calculateIterationsForTime(maxThinkingTime);
    const result = this.mctsController.findBestMove(iterations);

    if (!result) {
      throw new Error("Dion's AI failed to find a valid move");
    }

    const thinkingTime = Date.now() - startTime;

    // Update stats
    this.stats.movesCalculated++;
    this.totalThinkingTime += thinkingTime;
    this.stats.averageThinkingTime = this.totalThinkingTime / this.stats.movesCalculated;

    // Get analysis data for confidence calculation
    const analysisData = this.mctsController.getLatestAnalysisDataDirectly();
    const confidence = this.calculateConfidence(analysisData);

    return {
      fromPosition: result.fromPosition,
      move: result.move,
      confidence,
      thinkingTime,
      reasoning: analysisData?.chosenMove || "Priority-based decision",
      decisionLog: analysisData?.preprocessingLog,
    };
  }

  getStats(): AIStats {
    return { ...this.stats };
  }

  reset(): void {
    this.stats = {
      movesCalculated: 0,
      averageThinkingTime: 0,
      nodesEvaluated: 0,
    };
    this.totalThinkingTime = 0;
  }

  configure(config: Record<string, any>): void {
    // Optional: Could expose MCTS configuration parameters
    if (config.debugMode !== undefined) {
      this.mctsController.setDebugMode(config.debugMode);
    }
  }

  /**
   * Calculate number of MCTS iterations based on available time
   */
  private calculateIterationsForTime(maxTime: number): number {
    // Rough heuristic: ~500 iterations per second for MCTS
    // This is conservative to ensure we don't timeout
    return Math.floor((maxTime / 1000) * 500);
  }

  /**
   * Calculate confidence based on MCTS analysis
   */
  private calculateConfidence(analysisData: any): number {
    if (!analysisData) return 0.5;

    // If MCTS was performed, we have higher confidence
    if (analysisData.mctsPerformed) {
      // More iterations = higher confidence
      const iterations = analysisData.iterations || 0;
      return Math.min(0.95, 0.6 + (iterations / 10000) * 0.35);
    }

    // Priority-based decision confidence depends on the priority level
    if (analysisData.chosenMove && analysisData.chosenMove.includes("Priority")) {
      // Extract priority number if possible
      const priorityMatch = analysisData.chosenMove.match(/Priority (\d+)/);
      if (priorityMatch) {
        const priority = parseInt(priorityMatch[1]);
        // Lower priority number = higher confidence (1-15 are forced moves)
        if (priority <= 15) return 0.95; // Very high confidence for forced moves
        if (priority <= 20) return 0.8; // High confidence for tactical moves
        return 0.65; // Medium confidence for positional moves
      }
    }

    return 0.5; // Default confidence
  }
}